/**
 * `line` template — agent-computed trajectories (static data; scenario comparisons,
 * projections), with `RefLine` annotations (e.g. the pick-face ceiling) drawn across the
 * domain they extend. Ticks run along both axes and each is titled from the spec's own
 * `x_label`/`y_label` — a series carries no field metadata, so those labels are the only thing
 * that can say what its numbers are, and a view that omits one simply goes untitled on that
 * axis rather than being given a unit it can't back. Hovering or focusing a point shows its
 * exact (x, y) and series name.
 */

import { useState } from "react";

import type { LineView } from "@/lib/api";

import { AXIS, AXIS_LABEL, niceTicks, spacedTicks } from "./axis";
import { AxisTitle } from "./axis-title";
import { ChartTooltip } from "./chart-tooltip";
import { GRID_LINE, HIGHLIGHT_PRIMARY, HIGHLIGHT_SECONDARY, INK, NPI_GOLD } from "./colors";
import { useChartSize } from "./use-chart-size";
import type { ViewChrome, ViewSize } from "./view-shell";

const SERIES_COLORS = [HIGHLIGHT_PRIMARY, HIGHLIGHT_SECONDARY, NPI_GOLD, "#5C6769"];
const MAX_X_TICKS_CARD = 4;
const MAX_X_TICKS_EXPANDED = 8;

type LineAnnotation = NonNullable<LineView["annotations"]>[number];

function isRefLine(a: LineAnnotation): a is Extract<LineAnnotation, { kind: "refline" }> {
  return a.kind === "refline";
}

/** Round-ish y ticks spanning `[lo, hi]` — `niceTicks` only starts at 0, which is right for the
 *  common "projection from zero" case; a negative `lo` (a scenario that dips below zero) gets
 *  its own exact tick appended rather than a second nice-number pass over a signed range. */
function yTicksFor(lo: number, hi: number, count: number): number[] {
  const positive = niceTicks(hi, count).filter((t) => t <= hi);
  return lo < 0 ? [lo, ...positive] : positive;
}

interface LineChartProps {
  spec: LineView;
  size: ViewSize;
  chrome?: ViewChrome;
}

interface HoverPoint {
  seriesName: string;
  x: number;
  y: number;
  color: string;
}

export function LineChart({ spec, size, chrome = "full" }: LineChartProps) {
  const [hover, setHover] = useState<HoverPoint | null>(null);
  const box = useChartSize();
  const isCard = size === "card";
  // Drawn at the box's own pixel size — see `useChartSize`.
  const w = box.width;
  const h = box.height;
  const metrics = AXIS[size];
  const PAD = { l: metrics.padL, r: metrics.padR, t: metrics.padT, b: metrics.padB };
  const fontSize = metrics.font;
  const refLines = (spec.annotations ?? []).filter(isRefLine);

  const allX = spec.series.flatMap((s) => s.points.map((p) => p.x));
  const allY = [
    ...spec.series.flatMap((s) => s.points.map((p) => p.y)),
    ...refLines.filter((r) => r.axis === "y").map((r) => r.value),
  ];
  if (!allX.length || !allY.length) {
    return <p className="text-muted-foreground text-xs">This view carries no series data.</p>;
  }
  const x0 = Math.min(...allX);
  const x1 = Math.max(...allX);
  const y0 = Math.min(0, ...allY);
  const y1 = Math.max(...allY);
  const px = (x: number) => PAD.l + ((x - x0) / (x1 - x0 || 1)) * (w - PAD.l - PAD.r);
  const py = (y: number) => h - PAD.b - ((y - y0) / (y1 - y0 || 1)) * (h - PAD.t - PAD.b);

  const distinctX = [...new Set(allX)].sort((a, b) => a - b);
  const maxXTicks = isCard ? MAX_X_TICKS_CARD : MAX_X_TICKS_EXPANDED;
  const evenlySpacedX =
    distinctX.length <= maxXTicks
      ? distinctX
      : Array.from({ length: maxXTicks }, (_, i) => {
          const idx = Math.round((i * (distinctX.length - 1)) / (maxXTicks - 1));
          return distinctX[idx]!;
        });
  // Thinned again against the ACTUAL box: picking N ticks by index says nothing about how
  // much room their labels have once the card is narrow.
  const xTicks = spacedTicks(evenlySpacedX, px, fontSize * 4);
  const yTicks = yTicksFor(y0, y1, isCard ? 3 : 5);

  return (
    <div className="relative flex h-full min-h-[100px] min-w-0 flex-col gap-1">
      <div ref={box.ref} className="min-h-0 min-w-0 flex-1">
        {w > 0 && h > 0 ? (
          <svg width={w} height={h}>
            {yTicks.map((t) => (
              <g key={t}>
                <line
                  x1={PAD.l}
                  x2={w - PAD.r}
                  y1={py(t)}
                  y2={py(t)}
                  stroke={t === 0 ? INK : GRID_LINE}
                  strokeWidth={1}
                />
                <text
                  x={PAD.l - 4}
                  y={py(t) + fontSize / 3}
                  fontSize={fontSize}
                  textAnchor="end"
                  fill={AXIS_LABEL}
                >
                  {t.toLocaleString()}
                </text>
              </g>
            ))}
            <line x1={PAD.l} y1={PAD.t} x2={PAD.l} y2={h - PAD.b} stroke={INK} strokeWidth={1} />
            {xTicks.map((t) => (
              <text
                key={t}
                x={px(t)}
                y={h - PAD.b + fontSize + 4}
                fontSize={fontSize}
                textAnchor="middle"
                fill={AXIS_LABEL}
              >
                {t.toLocaleString()}
              </text>
            ))}
            {/* The spec's own axis titles: a line view's series carry no field metadata, so
              these are the only thing that says what its numbers are. */}
            {spec.y_label ? (
              <AxisTitle
                text={spec.y_label}
                x={fontSize}
                y={PAD.t + (h - PAD.t - PAD.b) / 2}
                fontSize={fontSize}
                rotate
              />
            ) : null}
            {spec.x_label ? (
              <AxisTitle
                text={spec.x_label}
                x={PAD.l + (w - PAD.l - PAD.r) / 2}
                y={h - fontSize / 2 - 2}
                fontSize={fontSize}
              />
            ) : null}
            {refLines.map((r, i) =>
              r.axis === "y" ? (
                <g key={i}>
                  <line
                    x1={PAD.l}
                    x2={w - PAD.r}
                    y1={py(r.value)}
                    y2={py(r.value)}
                    stroke={INK}
                    strokeDasharray="4 3"
                    strokeWidth={1}
                  />
                  <text
                    x={w - PAD.r}
                    y={py(r.value) - 3}
                    fontSize={fontSize}
                    textAnchor="end"
                    fill={INK}
                  >
                    {r.label}
                  </text>
                </g>
              ) : (
                <g key={i}>
                  <line
                    y1={PAD.t}
                    y2={h - PAD.b}
                    x1={px(r.value)}
                    x2={px(r.value)}
                    stroke={INK}
                    strokeDasharray="4 3"
                    strokeWidth={1}
                  />
                  <text x={px(r.value) + 3} y={PAD.t + 9} fontSize={fontSize} fill={INK}>
                    {r.label}
                  </text>
                </g>
              ),
            )}
            {spec.series.map((series, i) => {
              const color = SERIES_COLORS[i % SERIES_COLORS.length]!;
              const path = series.points
                .map((p, j) => `${j === 0 ? "M" : "L"}${px(p.x)} ${py(p.y)}`)
                .join(" ");
              return (
                <g key={series.name}>
                  <path d={path} fill="none" stroke={color} strokeWidth={2} />
                  {series.points.map((p, j) => (
                    <circle
                      key={j}
                      cx={px(p.x)}
                      cy={py(p.y)}
                      r={4}
                      fill={color}
                      tabIndex={0}
                      role="img"
                      aria-label={`${series.name}: ${p.x.toLocaleString()}, ${p.y.toLocaleString()}`}
                      className="cursor-default outline-none"
                      onMouseEnter={() => setHover({ seriesName: series.name, x: p.x, y: p.y, color })} // prettier-ignore
                      onMouseLeave={() => setHover((cur) => (cur?.x === p.x && cur.y === p.y && cur.seriesName === series.name ? null : cur))} // prettier-ignore
                      onFocus={() => setHover({ seriesName: series.name, x: p.x, y: p.y, color })}
                      onBlur={() => setHover((cur) => (cur?.x === p.x && cur.y === p.y && cur.seriesName === series.name ? null : cur))} // prettier-ignore
                    />
                  ))}
                </g>
              );
            })}
          </svg>
        ) : null}
      </div>
      {hover ? (
        <ChartTooltip x={`${(px(hover.x) / w) * 100}%`} y={`${(py(hover.y) / h) * 100}%`}>
          <div className="font-semibold">{hover.seriesName}</div>
          <div>
            {hover.x.toLocaleString()}, {hover.y.toLocaleString()}
          </div>
        </ChartTooltip>
      ) : null}
      <div className="mt-1 flex shrink-0 flex-wrap gap-3">
        {spec.series.map((series, i) => (
          <span
            key={series.name}
            className="text-muted-foreground flex items-center gap-1 text-[11px]"
          >
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ backgroundColor: SERIES_COLORS[i % SERIES_COLORS.length] }}
            />
            {series.name}
          </span>
        ))}
      </div>
      {chrome === "bare" ? null : (
        <p className="text-muted-foreground shrink-0 text-[11px] italic">
          computed at time of analysis
        </p>
      )}
    </div>
  );
}
